'use client';

import { formatDateTime } from '@pickleballinc/lib/format-dates';
import { MyModulesNoResults } from '@pickleballinc/ui/components/global/myModules/MyModulesNoResults';
import clsx from 'clsx';
import moment from 'moment';
import { useEffect, useMemo, useCallback, useState, type TouchEvent } from 'react';

import { publicEnv } from '@/lib/env';
import { WORLD_PICKLEBALL_RANKINGS } from '@/modules/rankings/constants';
import { NO_RESULTS_DESCRIPTION } from '@/modules/rankings/ppr/constants';
import {
  getGameStats,
  getSeedTopStats,
  parsePprDivisionType,
} from '@/modules/rankings/ppr/helpers';
import { useGetRankHistoryPlayerStats } from '@/modules/rankings/ppr/hooks';
import { CurrentSeedSkeleton } from '@/modules/rankings/ppr/player-profile/CurrentSeedSkeleton';
import { ProgressCard } from '@/modules/rankings/ppr/player-profile/ProgressCard';
import { RankingTooltip } from '@/modules/rankings/ppr/player-profile/RankingTooltip';
import { WorldPickleballRanking } from '@/modules/rankings/ppr/player-profile/WorldPickleballRanking';
import { RankingPlayerProfileData } from '@/modules/rankings/ppr/types';

interface CurrentSeedProps {
  currentSeedType: string;
  playerProfileData: RankingPlayerProfileData;
  queryParams: Record<string, string>;
}

export function CurrentSeed({ currentSeedType, playerProfileData, queryParams }: CurrentSeedProps) {
  const searchParams = useMemo(() => new URLSearchParams(queryParams), [queryParams]);

  const divisionType = searchParams.get('division_type');

  const [activeTab, setActiveTab] = useState(currentSeedType);
  const [isWeeks52TooltipOpen, setIsWeeks52TooltipOpen] = useState(false);

  useEffect(() => {
    setActiveTab(currentSeedType);
  }, [currentSeedType]);

  const isWorldRankingSelected = activeTab === WORLD_PICKLEBALL_RANKINGS;

  const { data, isFetching } = useGetRankHistoryPlayerStats(
    playerProfileData.playerUuid,
    divisionType,
    !isWorldRankingSelected && divisionType !== null
  );

  const playerRankingsData = data?.rankHistoryPlayerStats;

  const worldRankingDateRange = useMemo(() => {
    const today = moment();
    const start = today.clone().subtract(1, 'year');
    const end = today.clone().subtract(1, 'day');
    return `${formatDateTime(start.toDate())} - ${formatDateTime(end.toDate())}`;
  }, []);

  const handleWeeks52TouchStart = useCallback((e: TouchEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setIsWeeks52TooltipOpen(true);
  }, []);

  const closeTooltips = useCallback(() => {
    setIsWeeks52TooltipOpen(false);
  }, []);

  useEffect(() => {
    if (!isWeeks52TooltipOpen) return;
    document.addEventListener('touchstart', closeTooltips);
    return () => document.removeEventListener('touchstart', closeTooltips);
  }, [isWeeks52TooltipOpen, closeTooltips]);

  if (!isWorldRankingSelected && isFetching) {
    return <CurrentSeedSkeleton />;
  }

  if (!isWorldRankingSelected && !playerRankingsData) {
    return (
      <MyModulesNoResults
        description={NO_RESULTS_DESCRIPTION}
        cdnUrl={publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}
        descriptionTextColor='text-white'
        hideBorder
        isReservationsHomePage
      />
    );
  }

  const dateRange = isWorldRankingSelected
    ? worldRankingDateRange
    : (playerRankingsData?.seedStats.startToEndDate ?? '');

  const renderSeedStats = () => {
    const seedStats = playerRankingsData?.seedStats;
    if (!seedStats) return null;

    const seedTopStats = getSeedTopStats(activeTab, seedStats, parsePprDivisionType(divisionType));
    // TODO: Pass activeTab to getGameStats when Avg Partners Ranking and Avg Opponents Ranking are supported by BE, to conditionally hide them for Singles. For now, these two stats are hidden until supported by BE, so activeTab is not needed in getGameStats.
    const gameStats = getGameStats(seedStats);
    const matchesPct = parseInt(seedStats.matchOverview.matchesPercentage);
    const gamesPct = parseInt(seedStats.gamesOverview.matchesPercentage);

    return (
      <div className='grid gap-4 lg:grid-row-3 md:grid-row-2 grid-row-1'>
        <div className='flex flex-col overflow-hidden rounded-xl bg-white shadow-sm'>
          {seedTopStats.map((item, idx) => (
            <div
              key={item.label}
              className={clsx(
                'flex flex-1 flex-col items-center justify-center p-2',
                idx < seedTopStats.length - 1 && 'border-b border-gray-200'
              )}
            >
              <span className='text-sm text-gray-600 md:text-base'>{item.label}</span>
              <span className='text-base font-semibold text-gray-900 md:text-lg'>{item.value}</span>
              {item.subValue ? (
                <span className='text-xs font-semibold text-gray-500 md:text-sm'>
                  {item.subValue}
                </span>
              ) : null}
            </div>
          ))}
        </div>
        <div className='flex flex-col gap-4'>
          <div className='rounded-xl bg-white shadow-sm'>
            <ProgressCard
              percentage={matchesPct}
              title='Matches (W-L)'
              points={`${seedStats.matchOverview.matchesWinNum} - ${seedStats.matchOverview.matchesLossNum}`}
            />
          </div>
          <div className='rounded-xl bg-white shadow-sm'>
            <ProgressCard
              percentage={gamesPct}
              title='Games (W-L)'
              points={`${seedStats.gamesOverview.matchesWinNum} - ${seedStats.gamesOverview.matchesLossNum}`}
            />
          </div>
        </div>
        <div className='flex flex-col overflow-hidden rounded-xl bg-white shadow-sm'>
          <div className='flex flex-1 items-center justify-center border-b border-gray-200 bg-gray-50 p-3'>
            <span className='text-sm font-bold uppercase tracking-wide text-gray-700 md:text-base'>
              Game Stats
            </span>
          </div>

          {gameStats.map((item, idx) => (
            <div
              key={item.label}
              className={clsx(
                'flex flex-1 flex-col items-center justify-center p-2',
                idx < gameStats.length - 1 ? 'border-b border-gray-200' : ''
              )}
            >
              <span className='text-sm text-gray-600 md:text-base'>{item.label}</span>
              <span className='text-base font-semibold text-gray-900 md:text-lg'>{item.value}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className='bg-white font-sans text-gray-900'>
      <div className='mx-auto max-w-7xl'>
        <div className='mb-8 bg-brand-25 px-4 py-4 md:rounded-xl md:px-24'>
          <div className='mb-6 flex items-center gap-2'>
            <h3 className='text-lg font-bold text-gray-900 md:text-xl'>{activeTab}</h3>
            <RankingTooltip
              open={isWeeks52TooltipOpen}
              onOpenChange={setIsWeeks52TooltipOpen}
              handleTouch={handleWeeks52TouchStart}
              content={"52 Weeks is the accumulation of a player's points over the past 52 weeks"}
            />
          </div>

          <p className='-mt-5 mb-4 text-sm text-gray-600 md:text-base'>{dateRange}</p>

          {isWorldRankingSelected ? (
            <WorldPickleballRanking playerProfileData={playerProfileData} />
          ) : (
            renderSeedStats()
          )}
        </div>
      </div>
    </div>
  );
}
